
import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AccessibilitySettingsState {
  highContrastMode: boolean;
  largeTextMode: boolean;
  reduceMotion: boolean;
  setHighContrastMode: (enabled: boolean) => void;
  setLargeTextMode: (enabled: boolean) => void;
  setReduceMotion: (enabled: boolean) => void;
}

const useAccessibilityStore = create<AccessibilitySettingsState>()(
  persist(
    (set) => ({
      highContrastMode: false,
      largeTextMode: false,
      reduceMotion: false,
      setHighContrastMode: (enabled) => set({ highContrastMode: enabled }),
      setLargeTextMode: (enabled) => set({ largeTextMode: enabled }),
      setReduceMotion: (enabled) => set({ reduceMotion: enabled }),
    }),
    {
      name: 'accessibility-settings',
    }
  )
);

export const useAccessibilitySettings = () => {
  const store = useAccessibilityStore();

  // Apply settings to document
  useEffect(() => {
    const root = document.documentElement;
    
    // High contrast mode
    if (store.highContrastMode) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }

    // Large text mode
    if (store.largeTextMode) {
      root.classList.add('large-text');
    } else {
      root.classList.remove('large-text');
    }

    // Reduce motion
    if (store.reduceMotion) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }
  }, [store.highContrastMode, store.largeTextMode, store.reduceMotion]);

  // Apply zoom lock setting on mount
  useEffect(() => {
    const zoomStored = localStorage.getItem('app-zoom-enabled');
    if (zoomStored === 'false') {
      // Viewport meta
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, user-scalable=no, maximum-scale=1.0');
      }
      // CSS touch-action
      document.documentElement.style.touchAction = 'pan-x pan-y';
      // Safari gesture events
      const prevent = (e: Event) => e.preventDefault();
      document.addEventListener('gesturestart', prevent, { passive: false } as any);
      document.addEventListener('gesturechange', prevent, { passive: false } as any);
      // Multi-touch prevention
      const preventMulti = (e: TouchEvent) => { if (e.touches.length > 1) e.preventDefault(); };
      document.addEventListener('touchmove', preventMulti, { passive: false });
    }
  }, []);

  // Check for system preference on mount
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const prefersHighContrast = window.matchMedia('(prefers-contrast: more)');
    
    // Only set if user hasn't explicitly chosen
    const stored = localStorage.getItem('accessibility-settings');
    if (!stored) {
      if (prefersReducedMotion.matches) {
        store.setReduceMotion(true);
      }
      if (prefersHighContrast.matches) {
        store.setHighContrastMode(true);
      }
    }
  }, []);

  return store;
};
